import { useState, type FormEvent } from "react";
import {
  useMoneyAccounts,
  useOwed,
  useReimburse,
  type MoneyAccount,
  type OwedBill,
  type PersonOwed,
} from "@/api/cash";
import { useSetExpensePaidBy } from "@/api/finance";
import { ReimburseForm } from "@/components/forms/ReimburseForm";
import { RequestError } from "@/components/RequestError";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { SelectField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { formatDate, formatMoney } from "@/lib/format";
import { formatTotals } from "@/lib/money";
import { useSubmitState } from "@/lib/use-form";

type Mode = { kind: "reimburse"; person: PersonOwed } | { kind: "move"; bill: OwedBill } | null;

const TH = "px-4 py-2 font-medium";
const THEAD =
  "border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground";
const TD = "whitespace-nowrap px-4 py-2 text-right tabular-nums";

function state(bill: OwedBill): { label: string; tone: "success" | "warning" | "muted" } {
  if (bill.reimbursed_on) {
    return { label: `Repaid ${formatDate(bill.reimbursed_on)}`, tone: "success" };
  }
  if (bill.amount === null) return { label: "Not in this currency", tone: "muted" };
  if (Number(bill.repaid) > 0) return { label: "Part repaid", tone: "warning" };
  return { label: "Owed", tone: "warning" };
}

export function OwedPanel() {
  const [mode, setMode] = useState<Mode>(null);
  const owed = useOwed();
  const accounts = useMoneyAccounts();
  const reimburse = useReimburse();

  if (owed.isPending) return <p className="text-muted-foreground">Loading what is owed…</p>;
  if (owed.isError) return <RequestError error={owed.error} />;
  const people = owed.data.people;

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-muted-foreground">
        A bill someone paid from their own pocket is owed to them until PBO pays them back. Each
        payer is a person account (a director, Shibin, a company that paid on PBO&apos;s behalf);
        add one under Accounts. Total owed: {formatTotals(owed.data.total)}.
      </p>
      {mode?.kind === "reimburse" && accounts.data && (
        <Card className="border-primary/40">
          <CardHeader>
            <CardTitle>Reimburse {mode.person.name}</CardTitle>
          </CardHeader>
          <CardContent>
            <ReimburseForm
              person={mode.person}
              accounts={accounts.data}
              onSubmit={async (input) => {
                await reimburse.mutateAsync({ accountId: mode.person.account_id, input });
                setMode(null);
              }}
              onCancel={() => setMode(null)}
            />
          </CardContent>
        </Card>
      )}
      {mode?.kind === "move" && accounts.data && (
        <Card className="border-primary/40">
          <CardHeader>
            <CardTitle>Who paid {mode.bill.description}?</CardTitle>
            <CardDescription>
              The bill moves to that person: they are owed it, here and in the books.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <PaidByForm bill={mode.bill} accounts={accounts.data} onDone={() => setMode(null)} />
          </CardContent>
        </Card>
      )}
      {people.length === 0 ? (
        <EmptyState
          title="PBO owes nobody"
          description="Bills paid from someone's own pocket show here until they are repaid."
        />
      ) : (
        people.map((person) => (
          <PersonCard
            key={person.account_id}
            person={person}
            busy={mode !== null}
            onReimburse={() => setMode({ kind: "reimburse", person })}
            onMove={(bill) => setMode({ kind: "move", bill })}
          />
        ))
      )}
    </div>
  );
}

function PersonCard({
  person,
  busy,
  onReimburse,
  onMove,
}: {
  person: PersonOwed;
  busy: boolean;
  onReimburse: () => void;
  onMove: (bill: OwedBill) => void;
}) {
  const money = (amount: string) => formatMoney(amount, person.currency);
  const owes = Number(person.owed) > 0;
  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
        <div>
          <CardTitle>{person.name}</CardTitle>
          <CardDescription>
            {owes ? `PBO owes ${money(person.owed)}` : "Nothing owed"}
            {person.archived && " · archived: restore the account under Accounts to reimburse"}
          </CardDescription>
        </div>
        {owes && !person.archived && (
          <Button size="sm" onClick={onReimburse} disabled={busy}>
            Reimburse
          </Button>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {person.bills.length > 0 && (
          <div className="overflow-x-auto rounded-lg border">
            <table className="data-table">
              <thead className={THEAD}>
                <tr>
                  <th className={TH}>Date</th>
                  <th className={TH}>Bill</th>
                  <th className={`${TH} text-right`}>Amount</th>
                  <th className={`${TH} text-right`}>Repaid</th>
                  <th className={`${TH} text-right`}>Left</th>
                  <th className={TH}>State</th>
                  <th className={TH}>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {person.bills.map((bill) => {
                  const shown = state(bill);
                  return (
                    <tr key={bill.expense_id} className="border-b last:border-0">
                      <td className="whitespace-nowrap px-4 py-2">{formatDate(bill.spent_on)}</td>
                      <td className="px-4 py-2">
                        {bill.description}
                        {bill.payee && (
                          <span className="block text-xs text-muted-foreground">{bill.payee}</span>
                        )}
                      </td>
                      <td className={TD}>
                        {bill.amount === null ? "—" : money(bill.amount)}
                        {bill.currency !== person.currency && (
                          <span className="block text-xs text-muted-foreground">
                            {formatMoney(bill.bill_amount, bill.currency)}
                          </span>
                        )}
                      </td>
                      <td className={TD}>{money(bill.repaid)}</td>
                      <td className={TD}>{money(bill.left)}</td>
                      <td className="px-4 py-2">
                        <Badge variant={shown.tone}>{shown.label}</Badge>
                      </td>
                      <td className="px-4 py-2 text-right">
                        {!bill.reimbursed_on && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onMove(bill)}
                            disabled={busy}
                          >
                            Who paid
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {person.repayments.length > 0 && (
          <div className="flex flex-col gap-1 text-sm">
            <p className="font-medium">Paid back</p>
            <ul className="flex flex-col gap-1 text-muted-foreground">
              {person.repayments.map((repayment) => (
                <li key={repayment.transfer_id}>
                  {formatDate(repayment.made_on)}: {money(repayment.amount)} from{" "}
                  {repayment.from_account_name}
                  {repayment.reference ? ` (${repayment.reference})` : ""}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PaidByForm({
  bill,
  accounts,
  onDone,
}: {
  bill: OwedBill;
  accounts: MoneyAccount[];
  onDone: () => void;
}) {
  const people = accounts.filter((a) => a.kind === "person" && !a.archived_at);
  const [payer, setPayer] = useState("");
  const move = useSetExpensePaidBy();
  const { error, errors, saving, run } = useSubmitState();

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(async () => {
      await move.mutateAsync({ id: bill.expense_id, paidFromId: payer, version: bill.version });
      onDone();
    });
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <RequestError error={error} />
      <SelectField
        id={`paid-by-${bill.expense_id}`}
        label="Paid by"
        value={payer}
        onChange={setPayer}
        options={people.map((a) => ({ value: a.id, label: a.name }))}
        placeholder="Choose who paid"
        error={errors.paid_from_id}
      />
      <FormActions
        saving={saving}
        submitLabel="Move the bill"
        onCancel={onDone}
        disabled={!payer}
      />
    </form>
  );
}
